import { useCallback, useEffect, useRef, useReducer } from "react";
import debounce from "lodash/debounce";
import Axios from "@/Axios";
import { useConfirm } from "@/components";
import { useAtom, atom } from "jotai";
import {
  loadingState,
  defaultLoadingState,
  dimLoadingState,
  defaultDimLoadingState,
} from "@/components/state";

// 쿠키 이름으로 쿠키 존재 여부 확인
export const useCheckCookie = (name) => {
  const confirm = useConfirm();
  return (trueFn = () => {}, falseFn = () => {}) => {
    Axios.post("/api/cookie/check", {
      name,
    }).then((res) => {
      if (res.data?.errMsg) {
        confirm(`${res.data.errMsg}\n(${res.data.errCode})`);
        return;
      }

      const has = res.data?.has;
      if (has) {
        trueFn();
      } else {
        falseFn();
      }
    });
  };
};

// 세션 쿠키 확인을 통해 로그인 여부를 확인한다
// js 문법으로 확인 불가능한 경우가 발생하여 구현했다
// 실행할 수 있는 함수를 반환하며, 그 함수는 requestIdleCallback의 id를 반환한다
// 리소스 최적화를 위해 unmount시에 cancelIdleCallback(id)를 호출하기를 바란다
export const useCheckLogin = () => {
  const checkCookie = useCheckCookie("hattomam_sess_id");
  // 세션 쿠키 여부 확인
  return (trueFn = () => {}, falseFn = () => {}) => {
    const id = requestIdleCallback(() => {
      checkCookie(trueFn, falseFn);
    });
    return id;
  };
};

export const useLoadComplete = () => {
  const [_, setLoad] = useAtom(loadingState);

  return () => {
    // 0.5초 후 실행
    const timer = setTimeout(() => {
      setLoad((s) => ({ ...s, isLoad: true }));
      clearTimeout(timer);
    }, 500);
  };
};

export const useLoadReset = () => {
  const [_, setLoad] = useAtom(loadingState);
  return () => {
    setLoad({ ...defaultLoadingState });
  };
};

export const useDimLoadComplete = () => {
  const [_, setLoad] = useAtom(dimLoadingState);
  return (callback) => {
    // 1.5초 후 실행
    const timer = setTimeout(() => {
      setLoad({ ...defaultDimLoadingState });
      clearTimeout(timer);

      if (callback) {
        // 콜백 지연 실행
        const _timer = setTimeout(() => {
          callback();
          clearTimeout(_timer);
        }, 500);
      }
    }, 1500);
  };
};

export const useDimLoading = () => {
  const [_, setLoad] = useAtom(dimLoadingState);
  return () => {
    setLoad((s) => ({ ...s, isLoad: false }));
  };
};

// API 호출을 제한한다
// 연속 호출시 첫 호출에만 반응한다
// 이후 0.5초 동안은 반응하지 않는다
export function useDebouncedCallback(callback) {
  // 최신 콜백을 유지하기 위한 ref
  const callbackRef = useRef(() => {});

  // 매 렌더링마다 callback이 최신 값으로 갱신됨
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // debounce된 함수 생성
  const debouncedFn = useCallback(
    debounce(
      (...args) => {
        if (typeof callbackRef.current === "function") {
          callbackRef.current(...args);
        }
      },
      500,
      { leading: true, trailing: false }
    ),
    []
  );

  // 컴포넌트 언마운트 시 debounce 취소
  useEffect(() => {
    return () => {
      debouncedFn.cancel();
    };
  }, [debouncedFn]);

  return debouncedFn;
}

// Object 형태의 데이터(key:value)를 커스텀 ref를 이용하여 atom에 저장한다.
// * 기능 검증 필요
export const newRefAtom = (initialObject) => {
  return initialObject
    ? atom(
        Object.fromEntries(
          Object.entries(initialObject).map(([k, v]) => [k, { current: v }])
        )
      )
    : null;
};

// newRefAtom으로 생성한 refAtom을 할당하여 커스텀 useAtom를 호출한다.
// state, setState 이외에 항상 최신 값을 보장하는 refState도 함께 반환한다.
// * 기능 검증 필요
export const useRefAtom = (refAtom) => {
  const [refState, setRefState] = useAtom(refAtom);
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  const state = Object.fromEntries(
    Object.entries(refState).map(([k, ref]) => [k, ref.current])
  );
  const setState = (valueOrUpdater) => {
    setRefState((prev) => {
      const next = { ...prev };
      const value =
        typeof valueOrUpdater === "function"
          ? valueOrUpdater(next)
          : valueOrUpdater;
      let triggerRender = false;
      Object.entries(value).forEach(([k, v]) => {
        const target = next[k];
        if (target && "current" in target && target.current !== v) {
          target.current = v;
          triggerRender = true;
        }
      });
      if (triggerRender) {
        forceUpdate();
      }
      return next;
    });
  };
  return [state, setState, refState];
};
